import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { map, catchError, switchMap, tap, concatMap, toArray } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthenticationService } from '../authentication/authentication.service';
import { TimesheetsService } from '../timesheets/timesheets.service';

export interface YukiInvoiceResult {
    invoiceNumber: string | null;
    invoiceStatus: string | null;
    invoiceDate: string | null;
    yukiContactName: string | null;
    vatSource: string | null;
    message: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ComanageService {
  private httpOptions = {};

    constructor(private httpClient: HttpClient, private authenticationService: AuthenticationService, private timesheetsService: TimesheetsService) {
    this.httpOptions = authenticationService.getHeaders();
  }

  /** Check if a Comanage API key is present in backend settings */
  getKeyExists(): Observable<any> {
    return this.httpClient.get<any>(`${environment.baseUrl}/comanage/keyExists`, this.httpOptions);
  }

    getContacts(): any {
        return this.httpClient.get<any>(`${environment.baseUrl}/comanage/contacts`, this.httpOptions);
    }

  getCustomers(): any {
    return this.httpClient.get<any>(`${environment.baseUrl}/comanage/customers`, this.httpOptions);
  }

  getInvoices(): any {
    return this.httpClient.get<any>(`${environment.baseUrl}/comanage/invoices`, this.httpOptions);
  }

  getOffers(): any {
    return this.httpClient.get<any>(`${environment.baseUrl}/comanage/offers`, this.httpOptions);
  }

    getPdf(url: string): any {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/pdf');
        return this.httpClient.get(url, {
            headers: headers,
            responseType: 'blob',
        });
    }

    // Create invoice directly in Comanage API
    createInvoiceInComanage(invoiceData: any, apiKey: string): Observable<any> {
        const comanageUrl = 'https://api.comanage.me/v1/invoices';
        
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
        });
        
        return this.httpClient.post(comanageUrl, invoiceData, { headers });
    }

    // Add line items to an existing invoice in Comanage API
    addInvoiceItems(invoiceId: number | string, items: any[], apiKey: string): Observable<any> {
        const comanageUrl = `https://api.comanage.me/v1/invoices/${invoiceId}/lines`;
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
        });

        console.log(`Adding ${items.length} lines to invoice ${invoiceId} (sequential to preserve order)`);
        console.log('Lines payload:', items);

        if (!items || items.length === 0) {
            return of({ success: true });
        }

        // Post items sequentially to preserve order in the invoice
        return from(items).pipe(
            concatMap((item, idx) => {
                console.log(`→ Posting line ${idx + 1}/${items.length}`);
                return this.httpClient.post(comanageUrl, item, { headers }).pipe(
                    catchError((error) => {
                        console.error('Error adding single line:', error);
                        return of(null);
                    })
                );
            }),
            toArray(),
        );
    }

    // Finalize invoice in Comanage API (makes it "openstaand" status)
    finalizeInvoiceInComanage(invoiceId: number | string, apiKey: string): Observable<any> {
        const comanageUrl = `https://api.comanage.me/v1/invoices/${invoiceId}/finalize`;
        
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
        });
        
        console.log(`Finalizing invoice ${invoiceId}`);
        
        return this.httpClient.post(comanageUrl, {}, { headers });
    }

    // Set invoice intro text using one-column JSON body (single call)
    setInvoiceText(invoiceId: number | string, text: string, apiKey: string): Observable<any> {
        if (!text || text.trim().length === 0) {
            return of({ skipped: true });
        }

        const url = `https://api.comanage.me/v1/invoices/${invoiceId}/content/extra`;
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        });

        const body = {
            columns: 1,
            column_1: text.trim(),
        } as any;

        console.log('[CoManage] Patching invoice intro text at', url);
        return this.httpClient.patch(url, body, { headers }).pipe(
            tap((resp) => console.log('✅ [CoManage] Intro text patched:', resp)),
            catchError((err) => {
                console.error('❌ [CoManage] Failed to patch intro text:', err?.status, err?.message);
                throw err;
            })
        );
    }

    // Upload attachment to invoice in Comanage
    uploadInvoiceAttachment(invoiceId: number | string, fileUrl: string, fileName: string, apiKey: string, timeslotUuid?: string): Observable<any> {
        console.log(`🔼 [CoManage] Starting attachment upload for invoice ${invoiceId}`);
        console.log(`📄 File: ${fileName}`);
        console.log(`🔗 S3 URL: ${fileUrl}`);
        
        // Step 1: Retrieve file bytes — prefer direct S3 download via backend to avoid protected timesheets route
        const tryDownload$ = this.httpClient.request('GET', `${environment.baseUrl}/download/retrieveFile`, {
            ...this.httpOptions,
            body: fileUrl,
            responseType: 'json' as 'json'
        }).pipe(catchError(err => {
            console.warn('[CoManage] download/retrieveFile failed, falling back to timesheets UUID route:', err?.status, err?.message);
            return of(null);
        }));

        const retrieve$: Observable<any> = tryDownload$.pipe(
            switchMap(resp => {
                if (resp) return of(resp);
                if (timeslotUuid) {
                    return this.timesheetsService.getPdf(timeslotUuid);
                }
                throw new Error('Failed to retrieve file for attachment');
            })
        );

        return retrieve$.pipe(
            tap((response: any) => {
                console.log(`✅ [CoManage] File downloaded via backend, response keys:`, Object.keys(response));
            }),
            switchMap((response: any) => {
                // Convert base64 or byte array to Blob
                const pdfBytes = response.pdf;
                if (!pdfBytes) {
                    console.error(`❌ [CoManage] No pdf data in response:`, response);
                    throw new Error('No pdf data received from backend');
                }
                
                // Create Blob from byte array
                const byteArray = new Uint8Array(pdfBytes);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                console.log(`✅ [CoManage] Created blob from backend data, size: ${blob.size} bytes`);
                
                // Step 2: Upload file to CoManage /files endpoint
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${apiKey}`
                });

                const tryUpload = (url: string, fd: FormData) => {
                    console.log(`📤 [CoManage] Uploading file to ${url}`);
                    return this.httpClient.post(url, fd, { headers }).pipe(
                        tap((resp: any) => console.log('✅ [CoManage] File uploaded:', resp)),
                    );
                };

                // Primary payload (with collection)
                const formData1 = new FormData();
                formData1.append('name', fileName);
                formData1.append('type', 'application/pdf');
                formData1.append('collection', 'attachments');
                formData1.append('file', blob, fileName);

                // Fallback payload (just file)
                const formData2 = new FormData();
                formData2.append('file', blob, fileName);
                formData2.append('name', fileName);

                const candidates = [
                    { url: 'https://api.comanage.me/v1/files', fd: formData1 },
                    { url: 'https://api.comanage.me/files', fd: formData1 },
                    { url: `https://api.comanage.me/v1/invoices/${invoiceId}/attachments/upload`, fd: formData2 },
                    { url: `https://api.comanage.me/invoices/${invoiceId}/attachments/upload`, fd: formData2 },
                ];

                // Try endpoints in order until one succeeds
                const tryChain$ = candidates.reduce((chain$, c, idx) => {
                    if (idx === 0) {
                        return tryUpload(c.url, c.fd).pipe(
                            catchError(err => {
                                console.warn(`[CoManage] Upload failed at ${c.url}:`, err?.status, err?.message);
                                return of(null);
                            })
                        );
                    }
                    return chain$.pipe(
                        switchMap(prev => {
                            if (prev) return of(prev);
                            return tryUpload(c.url, c.fd).pipe(
                                catchError(err => {
                                    console.warn(`[CoManage] Upload failed at ${c.url}:`, err?.status, err?.message);
                                    return of(null);
                                })
                            );
                        })
                    );
                }, of(null));

                return tryChain$.pipe(
                    switchMap((fileResponse: any) => {
                        if (!fileResponse) {
                            throw new Error('All file upload attempts failed for CoManage');
                        }
                        // Step 3: Attach file to invoice
                        const fileUuid = fileResponse?.uuid 
                            || fileResponse?.data?.uuid 
                            || fileResponse?.media?.uuid;
                        
                        if (!fileUuid) {
                            console.error(`❌ [CoManage] No UUID in file upload response:`, fileResponse);
                            throw new Error('No file UUID returned from /files endpoint');
                        }
                        
                        const attachPayload = { uuid: fileUuid };
                        const attachCandidates = [
                            `https://api.comanage.me/v1/invoices/${invoiceId}/attachments/attach`,
                            `https://api.comanage.me/invoices/${invoiceId}/attachments/attach`,
                        ];
                        
                        const tryAttach = (url: string) => {
                            console.log(`📎 [CoManage] Attaching file to ${url}`);
                            return this.httpClient.post(url, attachPayload, { headers, responseType: 'text' as 'json' }).pipe(
                                tap((resp) => console.log('✅ [CoManage] Attachment linked:', resp)),
                                catchError((err) => {
                                    console.warn(`[CoManage] Attach failed at ${url}:`, err?.status, err?.message);
                                    return of(null);
                                })
                            );
                        };
                        
                        // Try attach endpoints sequentially
                        return attachCandidates.reduce((chain$, url, idx) => {
                            if (idx === 0) {
                                return tryAttach(url);
                            }
                            return chain$.pipe(
                                switchMap(prev => prev ? of(prev) : tryAttach(url))
                            );
                        }, of(null)).pipe(
                            switchMap((attachResp: any) => {
                                if (!attachResp) {
                                    throw new Error('All attachment attempts failed for CoManage');
                                }
                                return of(attachResp);
                            })
                        );
                    }),
                );
            }),
            catchError((error) => {
                console.error(`❌ [CoManage] Failed to download file via backend/timesheets:`, error);
                throw error;
            })
        );
    }

    /** Which system the backend books invoices in: the 'Facturen aanmaken in Yuki' Yuki setting. */
    getInvoiceTarget(): Observable<'COMANAGE' | 'YUKI'> {
        return this.httpClient
            .get<{ target: 'COMANAGE' | 'YUKI' }>(`${environment.baseUrl}/yuki/sales-invoices/target`, this.httpOptions)
            .pipe(map((result) => (result?.target === 'YUKI' ? 'YUKI' : 'COMANAGE')));
    }

    /**
     * Books the same invoice and lines the dialog would send to CoManage, in Yuki, through the
     * backend. One call replaces create, lines, text and finalize.
     */
    createInvoiceInYuki(invoice: any, lines: any[], extraText: string): Observable<YukiInvoiceResult> {
        return this.httpClient.post<YukiInvoiceResult>(`${environment.baseUrl}/yuki/sales-invoices`, { invoice, lines, extraText }, this.httpOptions);
    }

    // Get API key from backend
    getComanageApiKey(): Observable<string | null> {
        return this.getKeyExists().pipe(
            map((result: any) => result.key)
        );
    }
}
